import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileDown, Trash2, ReceiptText } from "lucide-react";
import api from "../lib/api";

const INVOICE_STATUSES = ["DRAFT", "SENT", "PARTIALLY_PAID", "PAID", "OVERDUE", "CANCELLED", "VOID"];
const BADGE = { SENT: "bg-blue-100 text-blue-700", PARTIALLY_PAID: "bg-amber-100 text-amber-700", PAID: "bg-green-100 text-green-700", OVERDUE: "bg-red-100 text-red-600", CANCELLED: "bg-slate-200 text-slate-500", VOID: "bg-slate-200 text-slate-500", DRAFT: "bg-amber-100 text-amber-700" };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [pay, setPay] = useState(null);
  const [payBusy, setPayBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/invoices", { params: { status: status || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const setInvStatus = async (id, s) => {
    try { await api.patch(`/admin/invoices/${id}`, { status: s }); toast.success(`Invoice marked ${s}`); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/invoices/${inv.id}`); toast.success("Invoice deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const pdf = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };

  const recordPayment = async () => {
    const amount = parseFloat(pay.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid payment amount"); return; }
    setPayBusy(true);
    try {
      await api.post(`/admin/invoices/${pay.inv.id}/record-payment`, {
        amount, method: pay.method, reference: pay.reference, note: pay.note,
      });
      toast.success(`$${amount.toFixed(2)} recorded on ${pay.inv.invoice_number}`);
      setPay(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Payment failed"); }
    finally { setPayBusy(false); }
  };

  return (
    <div data-testid="admin-invoices-page">
      <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Invoices</h1>
      <select data-testid="invoice-status-filter" className="pg-input !py-2.5 w-[200px] mt-5" value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="">All statuses</option>
        {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[860px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Order</th>
              <th className="px-5 py-3">Total</th><th className="px-5 py-3">Paid</th><th className="px-5 py-3">Outstanding</th><th className="px-5 py-3">Due</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-5 py-8 text-center text-slate-400" data-testid="invoices-empty-state">No invoices yet. Generate one from an order.</td></tr>}
            {items.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-5 py-3.5 font-[Poppins] font-semibold text-[var(--pg-navy)]">{inv.invoice_number}</td>
                <td className="px-5 py-3.5">{inv.customer_company || inv.customer_name}</td>
                <td className="px-5 py-3.5 text-slate-500">{inv.order_number}</td>
                <td className="px-5 py-3.5 font-semibold">{money(inv.total)}</td>
                <td className="px-5 py-3.5 text-green-700 font-medium" data-testid={`invoice-paid-${inv.id}`}>{money(inv.amount_paid || 0)}</td>
                <td className={`px-5 py-3.5 font-medium ${(inv.total - (inv.amount_paid || 0)) > 0.005 && !["VOID", "CANCELLED"].includes(inv.status) ? "text-amber-700" : "text-slate-400"}`} data-testid={`invoice-outstanding-${inv.id}`}>
                  {["VOID", "CANCELLED"].includes(inv.status) ? "—" : money(Math.max(0, inv.total - (inv.amount_paid || 0)))}
                </td>
                <td className="px-5 py-3.5 text-slate-500">{inv.due_date}</td>
                <td className="px-5 py-3.5">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${BADGE[inv.status]}`}>{inv.status}</span>
                  {inv.status === "PAID" && inv.payment_details && (
                    <div data-testid={`invoice-paid-info-${inv.id}`} className="mt-1 text-[10.5px] text-slate-400">
                      {inv.payment_details.card_brand ? `${inv.payment_details.card_brand.toUpperCase()} •••• ${inv.payment_details.card_last4}` : inv.payment_details.method}
                      {inv.payment_details.date ? ` · ${new Date(inv.payment_details.date).toLocaleDateString()}` : ""}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3.5 whitespace-nowrap text-right">
                  <select data-testid={`invoice-status-select-${inv.id}`} title="Set invoice status"
                    className="pg-input !py-1.5 !px-2 !text-[12px] !w-auto inline-block align-middle mr-1"
                    value={inv.status} onChange={(e) => setInvStatus(inv.id, e.target.value)}>
                    {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  {inv.payment_details?.receipt_url && (
                    <a data-testid={`invoice-receipt-${inv.id}`} href={inv.payment_details.receipt_url} target="_blank" rel="noreferrer" title="View Stripe receipt"
                      className="text-slate-500 hover:text-green-700 p-1.5 inline-flex align-middle"><ReceiptText size={16} /></a>
                  )}
                  <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => pdf(inv)} title="Download PDF" className="text-slate-500 hover:text-[var(--pg-accent)] p-1.5" aria-label="PDF"><FileDown size={16} /></button>
                  {inv.status !== "PAID" && inv.status !== "VOID" && (
                    <button data-testid={`invoice-record-payment-${inv.id}`}
                      onClick={() => setPay({ inv, amount: Math.max(0, inv.total - (inv.amount_paid || 0)).toFixed(2), method: "Wire", reference: "", note: "" })}
                      className="ml-1 text-[11.5px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200">Record Payment</button>
                  )}
                  {inv.status !== "VOID" && inv.status !== "PAID" && (
                    <button data-testid={`invoice-void-${inv.id}`} onClick={() => setInvStatus(inv.id, "VOID")}
                      className="ml-1 text-[11.5px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200">Void</button>
                  )}
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)} title="Delete invoice" className="ml-1 text-slate-500 hover:text-red-600 p-1.5" aria-label="Delete invoice"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="record-payment-modal" onClick={() => !payBusy && setPay(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-[Poppins] font-bold text-[17px] text-[var(--pg-navy)]">Record Payment — {pay.inv.invoice_number}</h3>
            <p className="mt-1 text-[13px] text-slate-500">Outstanding: <strong className="text-amber-700">{money(Math.max(0, pay.inv.total - (pay.inv.amount_paid || 0)))}</strong> of {money(pay.inv.total)}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div><label className="pg-label">Amount ($) *</label><input data-testid="payment-amount-input" type="number" step="0.01" min="0" className="pg-input !py-2.5" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} /></div>
              <div>
                <label className="pg-label">Method</label>
                <select data-testid="payment-method-select" className="pg-input !py-2.5" value={pay.method} onChange={(e) => setPay((p) => ({ ...p, method: e.target.value }))}>
                  {["Wire", "ACH", "Check", "Card", "Cash", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2"><label className="pg-label">Reference #</label><input data-testid="payment-reference-input" placeholder="e.g. wire confirmation, check number" className="pg-input !py-2.5" value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} /></div>
              <div className="col-span-2"><label className="pg-label">Note</label><input data-testid="payment-note-input" placeholder="Optional note" className="pg-input !py-2.5" value={pay.note} onChange={(e) => setPay((p) => ({ ...p, note: e.target.value }))} /></div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button data-testid="payment-cancel-button" onClick={() => setPay(null)} disabled={payBusy} className="text-[13px] font-[Poppins] font-semibold text-slate-500 px-4 py-2.5">Cancel</button>
              <button data-testid="payment-confirm-button" onClick={recordPayment} disabled={payBusy} className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">{payBusy ? "Recording..." : "Record Payment"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
